import {
  Alert,
  Box,
  BreadcrumbList,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Icon,
  Link,
  TabItem,
  Tabs,
  theme,
  Typography,
} from '@fe-toolkit/web-components/ssr'

import { convertMbpsToGbps, getBadgeForPlan, ProgressSteps, TabsWrapper } from '@giffgaff/broadband-common'

import { customEvent } from '@fe-toolkit/eventing'

import { useFeatureEnabled } from '@fe-toolkit/utility/client/tracking-context'

import getProductEventEntity from '@/utils/eventing/getProductEventEntity'

import { useEffectOnceWithCondition } from '@/hooks/useEffectOnceWithCondition'

import { cannotChangeReasons, formatCannotChangeInfo, getNotificationCopy } from '@/utils/formatPlanAmendments'

import { basePath } from '@/constants'

import { formatPriceFromPenceToPounds } from '@/utils/formatPrice'

import ProfilePageLoader from '@/components/molecules/ProfilePageLoader'
import { IconList, IconListItem, IconListItemWrapper } from '@/components/molecules/IconList'

import ChangePlanNoContent from '../components/ChangePlanNoContent'
import ChangePlanNotification from '../components/ChangePlanNotification'
import { type AmendmentProducts, ChangePlanSelectionState, FormattedProductForPlan } from './types'

import { ProductForPlan } from '../types'
import { changePlanProgressSteps } from '../consts'
import ChangePlanSelectionProductCard from './components/ChangePlanSelectionProductCard'
import { ContractsPriceFreezeBenefitsTable } from './components/ContractsPriceFreezeBenefitsTable'

export type ChangePlanPageProps = ChangePlanSelectionState

const tabsClassName = 'broadband-change-plan-tabs'

const ChangePlanSelection = ({
  amendmentDetails,
  amendmentProducts,
  currentPlan,
  newPlan,
  productsByContract,
  status,
}: ChangePlanPageProps) => {
  const { enabled: offerFlagEnabled } = useFeatureEnabled('broadband-five-pound-price-offer')

  useEffectOnceWithCondition(
    ([productsByContract, currentPlan]) => {
      customEvent('bb_change_plan_journey_impression', {
        contextType: 'choose_plan',
        currentPlanId: currentPlan?.id ?? '',
        skus: productsByContract
          ? productsByContract
              .flatMap(option =>
                option.products.map(product => {
                  const eventEntity = getProductEventEntity(product)

                  return eventEntity.sku
                }),
              )
              .filter((sku): sku is string => typeof sku === 'string')
          : [],
      })
    },
    ([_0, _1, status]) => status !== 'idle' && status !== 'loading',
    [productsByContract, currentPlan, status] as const,
  )

  const monthToMonthOption = productsByContract?.find(
    contract => !contract.title || contract.title === 'Monthly rolling',
  )
  const monthToMonthProducts = formatProducts(monthToMonthOption?.products, currentPlan, status)
  const contractOption24M = productsByContract?.find(contract => contract.title === '24 months')
  const contract24MProducts = formatProducts(contractOption24M?.products, currentPlan, status)
  const cannotChangeInfo = formatCannotChangeInfo(newPlan, amendmentDetails)
  const cannotChangeNotificationCopy = getNotificationCopy(amendmentDetails?.reason)
  const planEndsAt = currentPlan?.provisionalContractEndDate
    ? new Date(currentPlan.provisionalContractEndDate).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  const hasProductReasonCodes =
    amendmentProducts && Object.values(amendmentProducts.products).some(product => !product.valid)

  const activePlan = [...monthToMonthProducts, ...contract24MProducts].find(p => p.hasActivePlan)
  const monthToMonthProductsForGrid = monthToMonthProducts.filter(p => !p.hasActivePlan)
  const contract24MProductsForGrid = contract24MProducts.filter(p => !p.hasActivePlan)
  const maxDifferenceInPence = contractOption24M?.maxDifferenceInPence
  const tabHighlightText =
    offerFlagEnabled || maxDifferenceInPence === undefined || maxDifferenceInPence <= 0
      ? 'Limited time offer'
      : `Save up to ${formatPriceFromPenceToPounds(maxDifferenceInPence)} a month`

  return (
    <article>
      <Container
        as="section"
        backgroundColor="wildSand"
        sx={({ theme }) => ({
          padding: `${theme.spacing.medium} 0`,
          [theme.breakpoints.up('medium')]: {
            padding: `${theme.spacing.large} 0`,
          },
        })}
      >
        <BreadcrumbList
          items={[
            {
              href: basePath,
              name: 'Account',
              onClick: () => {
                customEvent('gg_click', { contextType: 'account' })
              },
            },
            { name: 'Change plan' },
          ]}
        />
        <Box
          sx={({ theme }) => ({
            marginTop: theme.spacing.medium,
            [theme.breakpoints.up('medium')]: {
              marginTop: theme.spacing.large,
            },
          })}
        >
          <ProgressSteps currentStep={0} steps={changePlanProgressSteps}></ProgressSteps>
        </Box>

        <Flex
          sx={({ theme }) => ({
            flexDirection: 'column',
            gap: theme.spacing.medium,
            marginTop: theme.spacing.medium,
            [theme.breakpoints.up('medium')]: {
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: theme.spacing.large,
              marginTop: theme.spacing.large,
            },
          })}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              as="h1"
              sx={({ theme }) => ({
                marginBottom: theme.spacing.small,
                [theme.breakpoints.up('medium')]: {
                  marginBottom: theme.spacing.medium,
                },
              })}
              testId="change-plan-title"
              variant="megaphone"
            >
              Change plan
            </Typography>
            <IconList>
              <IconListItem>
                <IconListItemWrapper>
                  <Icon height={'100%'} iconKey="Calendar" width={'100%'} />
                </IconListItemWrapper>
                Change plan once a month
              </IconListItem>
              <IconListItem>
                <IconListItemWrapper>
                  <Icon height={'100%'} iconKey="AdjustableControls" width={'100%'} />
                </IconListItemWrapper>
                Move up to go faster or down to save a bit of cash
              </IconListItem>
              <IconListItem>
                <IconListItemWrapper>
                  <Icon height={'100%'} iconKey="TearUpContract" width={'100%'} />
                </IconListItemWrapper>
                No sneaky price rises
              </IconListItem>
            </IconList>
          </Box>

          {activePlan && (
            <Box
              data-testid="active-plan-card"
              sx={({ theme }) => ({
                [theme.breakpoints.up('medium')]: {
                  flexShrink: 0,
                  width: '33%',
                },
              })}
            >
              <Typography as="p" variant="speakUp">
                Your current plan
              </Typography>
              <Box sx={{ marginTop: theme.spacing.tiny }}>
                <ChangePlanSelectionProductCard
                  currentPlan={currentPlan!}
                  product={{
                    ...activePlan,
                    benefits: [],
                  }}
                />
              </Box>
            </Box>
          )}
        </Flex>
      </Container>

      <Container
        as="section"
        backgroundColor="wildSand"
        sx={({ theme }) => ({
          paddingBottom: theme.spacing.medium,
          [theme.breakpoints.up('medium')]: { paddingBottom: theme.spacing.large },
          overflow: 'hidden',
        })}
      >
        <Flex
          flexDirection="column"
          sx={({ theme }) => ({
            gap: theme.spacing.medium,
            [theme.breakpoints.up('medium')]: {
              gap: theme.spacing.large,
            },
          })}
        >
          {status === 'loading' && <ProfilePageLoader />}

          {(status === 'empty' || status === 'fetch_failed') && (
            <ChangePlanNoContent>There are no plans to choose from at the moment</ChangePlanNoContent>
          )}

          {status === 'cannot_change' && (
            <ChangePlanNotification>
              <Typography as="p" data={cannotChangeInfo} variant="speakUp">
                {cannotChangeNotificationCopy.title}
              </Typography>
              <Typography as="p" data={cannotChangeInfo} variant="speak">
                {cannotChangeNotificationCopy.copy}
              </Typography>
            </ChangePlanNotification>
          )}
          {(status === 'success' || status === 'cannot_change') && (
            <TabsWrapper
              sx={{
                [`.${tabsClassName}_tab-button .titleHighlight`]: {
                  background: theme.colors.alto,
                },
                [`.${tabsClassName}_tab-button--active .titleHighlight`]: {
                  background: theme.colors.pink,
                },
                [`.${tabsClassName}_tab-button--active .titleText`]: {
                  fontWeight: 600,
                },
              }}
              tabsClassName={tabsClassName}
            >
              <Tabs
                ariaLabel="Contract options"
                className={tabsClassName}
                defaultTabIndex={currentPlan.contractDurationText === '24 months' ? 1 : 0}
              >
                <TabItem
                  id="month-to-month"
                  tabTitle={
                    <Typography as="p" className="titleText">
                      Monthly rolling
                    </Typography>
                  }
                >
                  <Flex
                    flexDirection="column"
                    sx={({ theme }) => ({
                      gap: theme.spacing.medium,
                      [theme.breakpoints.up('medium')]: {
                        gap: theme.spacing.large,
                      },
                    })}
                  >
                    <ChangePlanProductAlerts amendmentProducts={amendmentProducts} products={monthToMonthProducts} />
                    {monthToMonthProductsForGrid.length > 0 ? (
                      <>
                        {!hasProductReasonCodes && (
                          <Box
                            backgroundColor={theme.colors.wildSand}
                            borderRadius={theme.borderRadius.tiny}
                            padding="tiny"
                          >
                            <Flex alignItems="center" gap={theme.spacing.tiny}>
                              <Box
                                sx={{
                                  transform: 'rotate(-12deg)',
                                }}
                              >
                                <Icon
                                  height={theme.spacing.large}
                                  iconKey="LockedPadlock"
                                  width={theme.spacing.large}
                                />
                              </Box>
                              <div>
                                <Typography as="p" variant="speakUp">
                                  2026 price lock
                                </Typography>
                                <Typography as="p" variant="speak">
                                  Flexible plans. Fixed prices.
                                </Typography>
                              </div>
                            </Flex>
                          </Box>
                        )}
                        <Grid columnGap="small" gridColumns={6} rowGap="small">
                          {monthToMonthProductsForGrid.map((product, index) => (
                            <GridItem
                              key={`grid-item-${product.productId}`}
                              data-testid={`plan-card-${index}`}
                              large={2}
                              tiny={6}
                            >
                              <ChangePlanSelectionProductCard currentPlan={currentPlan} product={product} />
                            </GridItem>
                          ))}
                        </Grid>
                        <Box maxWidth="680px">
                          <Typography as="p" variant="whisper">
                            You can upgrade to a faster speed or downgrade to a slower speed once per month. If you
                            change plans, including returning to your original plan, the price available for the plan
                            you change to, at the time of the change, will apply.
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <ChangePlanNotification>
                        <Typography as="p" variant="speakUp">
                          You can't change to a monthly rolling plan at the moment.
                        </Typography>
                        {planEndsAt && (
                          <Typography as="p" variant="speak">
                            Your fixed term comes to an end on {planEndsAt}. Check back here then to switch to a monthly
                            rolling plan.
                          </Typography>
                        )}
                        <Button isSmall className="launch_intercom_messenger">
                          Chat with us
                        </Button>
                      </ChangePlanNotification>
                    )}
                  </Flex>
                </TabItem>

                <TabItem
                  id="monthly-payment"
                  tabTitle={
                    <>
                      <Typography as="p" className="titleHighlight" variant="whisperLoud">
                        {tabHighlightText}
                      </Typography>
                      <Typography as="p" className="titleText">
                        {contractOption24M?.title ?? '24 months'}
                      </Typography>
                    </>
                  }
                >
                  <Flex
                    flexDirection="column"
                    sx={({ theme }) => ({
                      gap: theme.spacing.medium,
                      [theme.breakpoints.up('medium')]: {
                        gap: theme.spacing.large,
                      },
                    })}
                  >
                    <ChangePlanProductAlerts amendmentProducts={amendmentProducts} products={contract24MProducts} />
                    {contract24MProductsForGrid.length > 0 ? (
                      <>
                        {!hasProductReasonCodes && (
                          <Box
                            backgroundColor={theme.colors.gallery}
                            borderRadius={theme.borderRadius.small}
                            padding="tiny"
                          >
                            <Flex alignItems="center" gap={theme.spacing.tiny}>
                              <Box
                                sx={{
                                  transform: 'rotate(-12deg)',
                                }}
                              >
                                <Icon
                                  height={theme.spacing.large}
                                  iconKey="GoodybagThumbsUp"
                                  width={theme.spacing.large}
                                />
                              </Box>
                              <div>
                                <Typography as="p" variant="speakUp">
                                  No sneaky price hikes
                                </Typography>
                                <Typography as="p" variant="speak">
                                  For the entire 24 month term
                                </Typography>
                              </div>
                            </Flex>
                          </Box>
                        )}
                        <Grid columnGap="small" gridColumns={6} rowGap="small">
                          {contract24MProductsForGrid.map((product, index) => (
                            <GridItem
                              key={`grid-item-${product.productId}`}
                              data-testid={`plan-card-${index}`}
                              large={2}
                              tiny={6}
                            >
                              <ChangePlanSelectionProductCard currentPlan={currentPlan} product={product} />
                            </GridItem>
                          ))}
                        </Grid>
                        <Flex
                          flexDirection="column"
                          sx={({ theme }) => ({
                            gap: theme.spacing.small,
                            maxWidth: theme.layout.readableWidth,
                            [theme.breakpoints.up('medium')]: {
                              gap: theme.spacing.medium,
                            },
                          })}
                        >
                          {offerFlagEnabled && (
                            <>
                              <Link targetBlank href="https://www.giffgaff.com/terms/promotions#fibre-for-fiver">
                                See the full details in our terms here.
                              </Link>
                              <Typography as="p" variant="speak">
                                Prices available until 28/08/26.
                              </Typography>
                            </>
                          )}

                          <Typography variant="loudBold">No mid-term price rises</Typography>

                          <ContractsPriceFreezeBenefitsTable />

                          <Typography as="p" variant="speakUp">
                            Sign up for 24 months to get a lower price.
                          </Typography>
                          <Typography as="p" variant="speak">
                            Your price won't change for the entire term. No April hikes, no surprises. Longer
                            commitments help our planning and help us offer lower prices.
                          </Typography>
                          <Typography as="p" variant="whisper">
                            You can upgrade to a faster speed or downgrade to a slower speed once per month. If you
                            change plans, including returning to your original plan, the price available for the plan
                            you change to, at the time of the change, will apply.
                          </Typography>
                        </Flex>
                      </>
                    ) : (
                      <ChangePlanNoContent>There are no plans to choose from at the moment</ChangePlanNoContent>
                    )}
                  </Flex>
                </TabItem>
              </Tabs>
            </TabsWrapper>
          )}
        </Flex>
      </Container>
    </article>
  )
}

export default ChangePlanSelection

function formatProducts(
  products: ProductForPlan[] | undefined,
  currentPlan: ChangePlanPageProps['currentPlan'],
  status: ChangePlanPageProps['status'],
): FormattedProductForPlan[] {
  if (!products) return []

  return products
    .slice()
    .sort((a, b) => a.downstreamSpeedInMbps - b.downstreamSpeedInMbps) // sort by speed
    .map(product => {
      const bottomSlot =
        product.canAmend && !product.hasActivePlan && status !== 'cannot_change' ? (
          <Button
            isFullWidth={true}
            sx={({ theme }) => ({ marginBottom: theme.spacing.tiny })}
            variant="standard"
            onClick={() => {
              customEvent('bb_change_plan_journey_cta_click', {
                contextType: 'choose_plan',
                currentPlanId: currentPlan?.id ?? '',
                product: getProductEventEntity(product),
              })
              product.onSelect()
            }}
          >
            Select plan
          </Button>
        ) : undefined

      return {
        ...product,
        formattedSpeed: convertMbpsToGbps(product.downstreamSpeedInMbps),
        formattedPrice: formatPriceFromPenceToPounds(product.priceInPence),
        formattedPostPromotionPrice: product.postPromotionPriceInPence
          ? formatPriceFromPenceToPounds(product.postPromotionPriceInPence)
          : undefined,
        backgroundColor: (status === 'cannot_change' || (status === 'success' && product.hasActivePlan)
          ? 'gallery'
          : 'white') as 'gallery' | 'white',
        badgeContent: product.hasActivePlan ? getBadgeForPlan(true) : undefined,
        bottomSlot,
      }
    })
}

const ChangePlanProductAlerts = ({
  amendmentProducts,
  products,
}: {
  amendmentProducts: AmendmentProducts | undefined
  products: FormattedProductForPlan[]
}) => {
  const uniqueReasonCodes = products
    .map(product => {
      const amendmentProduct = amendmentProducts?.products[product.productId]

      return amendmentProduct && !amendmentProduct.valid ? amendmentProduct.reasonCode : null
    })
    .filter((reasonCode): reasonCode is string => reasonCode !== null)
    .filter((reasonCode, index, arr) => arr.indexOf(reasonCode) === index)

  return uniqueReasonCodes.map(reasonCode => {
    const contentForReasonCode = cannotChangeReasons[reasonCode as keyof typeof cannotChangeReasons]

    if (!contentForReasonCode) {
      return null
    }

    return (
      <Alert key={reasonCode} isMinor borderRadius="small" variant={contentForReasonCode.variant ?? 'error'}>
        {contentForReasonCode.title && (
          <Typography as="p" variant="speakUp">
            {contentForReasonCode.title}
          </Typography>
        )}
        <Typography as="p" variant="speak">
          {contentForReasonCode.copy}
        </Typography>
        {'href' in contentForReasonCode && 'linkText' in contentForReasonCode && (
          <Button isSmall href={contentForReasonCode.href}>
            {contentForReasonCode.linkText}
          </Button>
        )}
      </Alert>
    )
  })
}
